<script setup>
import { Link, useForm } from "@inertiajs/inertia-vue3";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import Checkbox from "primevue/checkbox";
import RadioButton from "primevue/radiobutton";
import Dropdown from "primevue/dropdown";
import Slider from "primevue/slider";
import Calendar from "primevue/calendar";
import {computed, ref} from "vue";
import InputMask from "primevue/inputmask";

const emits = defineEmits(["next"]);
const props = defineProps([
    "form",
    'errors',
    'stressLevelOptions',
    'v$',
]);

const sufferFrom = ref([
    "Müdigkeit",
    "Schlafstörungen",
    "Burnout",
    "Stresssymptomen",
]);
const sufferFrom2 = ref([
    "Schneller Erschöpfung",
    "Depressionen",
    "Long-Covid-Symptomen",
    "Kurzatmigkeit",
    'Lipödem',
]);
const sufferFrom3 = ref([
    "Asthma",
    "Platzangst",
    "Schweren Beinen",
    "Schmerzen in den Beinen",
    "Muskelschmerzen",
    "Rückenschmerzen",
    "Muskelkater, bereits nach kurzer muskulärer Beanspruchung",
    "Ödemen",
    "Bluthochdruck",
    "Durchblutungsstörungen",
    "Herzinsuffizienz",
]);
const sufferFrom4 = ref([
    "Krampfadern",
    "Venenentzündung",
    "Gereizte, entzündete Haut, Hauterkrankungen",
    "Psoriasis",
    "Entzündungen",
    "Hepatitis",
    "Infektion der Atemwege",
]);
const sufferFrom5 = ref([
    "Chronisch obstruktiver Lungenerkrankung COPD",
    "Niereninsuffizienz",
    "Schilddrüsen-Unterfunktion",
    "Diabetes",
    "Nabel-/Bauchwandhernie/Leistenbruch",
    "Inkontinenz",
    "Polyneuropathie",
]);
const onlyForWomen = ref([
    "Du bist schwanger",
    "Du stillst zur Zeit",
    "Du planst eine künstliche Befruchtung",
    "Du nutzt eine Kupferspirale zur Verhütung",
]);

</script>
<template>
    <div class="lato p-7 rounded my-2">
        <div class="px-2 text-xl font-semibold mb-7">
            Liegen bei dir Beschwerden oder Befunde vor?
        </div>

        <div class="my-7">
            <div class="font-bold">Leidest du unter:</div>
            <div class="grid grid-cols-2 gap-4 my-4">
                <div class="" v-for="(item, i) in sufferFrom" :key="i">
                    <div class="flex items-center gap-4">
                        <Checkbox
                            :disabled="form.is_no_findings == true"
                            v-model="form.symptoms"
                            :value="item" />

                        <div class="">{{ item }}</div>
                    </div>
                </div>

                <div></div>
                <div>
                    <label class="font-bold" >Wie hoch schätzt du dein Stresslevel ein?</label>

                    <Dropdown
                        :disabled="form.is_no_findings == true"
                        v-model="form.stress_level"
                        class="w-full"
                        :options="stressLevelOptions"
                        optionLabel="label"
                        optionValue="value"
                    ></Dropdown>
                </div>
            </div>
        </div>

        <div class="my-7">
            <div class="grid grid-cols-2 gap-4 my-4">
                <div class="" v-for="(item, i) in sufferFrom2" :key="i">
                    <div class="flex items-center gap-4">
                        <Checkbox
                            :disabled="form.is_no_findings == true"
                            v-model="form.symptoms"
                            :value="item" />
                        <div class="">{{ item }}</div>
                    </div>
                </div>

                <div class="flex items-center gap-4">
                    <div class="flex items-center gap-4">
                        <Checkbox
                            :disabled="form.is_no_findings == true"
                            v-model="form.symptoms"
                            value="Allergien" />
                        <div class="">Allergien</div>
                    </div>

                    <div class="flex-1">
                        <InputText
                            :disabled="form.is_no_findings == true"
                            :invalid="v$.form.allergies.$error"
                            v-model="form.allergies"
                            type="text"
                            class="w-full"
                            placeholder="welche"
                            @input="v$.form.allergies.$touch"
                            @blur="v$.form.allergies.$touch"
                        />

                        <div v-if="v$.form.allergies.$error" class="text-sm text-[#dc2626]">
                            {{ v$.form.allergies.$errors[0].$message }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="my-7">
            <div class="grid grid-cols-2 gap-4 my-4">
                <div v-for="(item, i) in sufferFrom3"
                     :key="i"
                     :class="`${item == 'Muskelkater, bereits nach kurzer muskulärer Beanspruchung' ? 'col-span-2' : ''}`">
                    <div class="flex items-center gap-4">
                        <Checkbox
                            :disabled="form.is_no_findings == true"
                            v-model="form.symptoms"
                            :value="item" />
                        <div class="">{{ item }}</div>
                    </div>
                </div>
            </div>
        </div>

        <div class="my-7">
            <div class="grid grid-cols-3 gap-4 my-4">
                <div class="flex items-center">
                    <div class="flex items-center gap-4">
                        <Checkbox
                            :disabled="form.is_no_findings == true"
                            v-model="form.symptoms"
                            value="Herzinfarkt"
                        />
                        <div class="">Herzinfarkt</div>
                    </div>
                </div>

                <div class="col-span-2">
                    <div class="flex items-center gap-2">
                        <span>wann:</span>

                        <InputMask
                            :disabled="form.is_no_findings == true"
                            :invalid="v$.form.heart_attack_date.$error"
                            v-model="form.heart_attack_date"
                            mask="99.9999"
                            placeholder="mm.jjjj"
                            @input="v$.form.heart_attack_date.$touch"
                            @blur="v$.form.heart_attack_date.$touch"
                            class="block w-full" />
                    </div>

                    <div v-if="v$.form.heart_attack_date.$error" class="text-sm text-[#dc2626]">
                        {{ v$.form.heart_attack_date.$errors[0].$message }}
                    </div>
                </div>
            </div>

            <div class="grid grid-cols-3 gap-4 my-4">
                <div class="flex items-center">
                    <div class="flex items-center gap-4">
                        <Checkbox
                            :disabled="form.is_no_findings == true"
                            v-model="form.symptoms"
                            value="Thrombose" />
                        <div class="">Thrombose</div>
                    </div>
                </div>

                <div class="col-span-2">
                    <div class="flex items-center gap-2">
                        <span>wann:</span>

                        <InputMask
                            :disabled="form.is_no_findings == true"
                            :invalid="v$.form.thrombosis_date.$error"
                            v-model="form.thrombosis_date"
                            mask="99.9999"
                            placeholder="mm.jjjj"
                            @input="v$.form.thrombosis_date.$touch"
                            @blur="v$.form.thrombosis_date.$touch"
                            class="block w-full" />
                    </div>

                    <div v-if="v$.form.thrombosis_date.$error" class="text-sm text-[#dc2626]">
                        {{ v$.form.thrombosis_date.$errors[0].$message }}
                    </div>
                </div>
            </div>
        </div>

        <div class="my-7">
            <div class="grid grid-cols-2 gap-4 my-4">
                <div
                    v-for="(item, i) in sufferFrom4"
                    :key="i"
                    :class="`${item == 'Gereizte, entzündete Haut, Hauterkrankungen' ? 'col-span-2' : ''}`"
                >
                    <div class="flex items-center gap-4">
                        <Checkbox
                            :disabled="form.is_no_findings == true"
                            v-model="form.symptoms"
                            :value="item" />

                        <div class="">{{ item }}</div>
                    </div>
                </div>

                <div class="flex items-center gap-4">
                    <div class="flex items-center gap-4">
                        <Checkbox
                            :disabled="form.is_no_findings == true"
                            v-model="form.symptoms"
                            value="Infektionskrankheit" />
                        <div class="">Infektionskrankheit</div>
                    </div>

                    <div class="flex-1">
                        <InputText
                            :disabled="form.is_no_findings == true"
                            :invalid="v$.form.which_infectious_disease.$error"
                            v-model="form.which_infectious_disease"
                            type="text"
                            class="w-full"
                            placeholder="welche"
                            @input="v$.form.which_infectious_disease.$touch"
                            @blur="v$.form.which_infectious_disease.$touch"
                        />

                        <div v-if="v$.form.which_infectious_disease.$error" class="text-sm text-[#dc2626]">
                            {{ v$.form.which_infectious_disease.$errors[0].$message }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="my-7">
            <div class="grid grid-cols-2 gap-4 my-4">
                <div
                    v-for="(item, i) in sufferFrom5"
                    :key="i"
                    :class="`${item == 'Chronisch obstruktiver Lungenerkrankung COPD' ? 'col-span-2' : ''}`"
                >
                    <div class="flex items-center gap-4">
                        <Checkbox
                            :disabled="form.is_no_findings == true"
                            v-model="form.symptoms"
                            :value="item" />
                        <div class="">{{ item }}</div>
                    </div>
                </div>

                <div class="flex items-center gap-4">
                    <div class="flex items-center gap-4">
                        <Checkbox
                            :disabled="form.is_no_findings == true"
                            v-model="form.symptoms"
                            value="Autoimmunerkrankung" />

                        <div class="">Autoimmunerkrankung</div>
                    </div>

                    <div class="flex-1">
                        <InputText
                            :disabled="form.is_no_findings == true"
                            :invalid="v$.form.which_autoimmune_disease.$error"
                            v-model="form.which_autoimmune_disease"
                            type="text"
                            class="w-full"
                            placeholder="welche"
                            @input="v$.form.which_autoimmune_disease.$touch"
                            @blur="v$.form.which_autoimmune_disease.$touch"
                        />

                        <div v-if="v$.form.which_autoimmune_disease.$error" class="text-sm text-[#dc2626]">
                            {{ v$.form.which_autoimmune_disease.$errors[0].$message }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="my-7">
            <div class="my-4">
                <div class="flex items-center gap-4">
                    <Checkbox
                        :disabled="form.is_no_findings == true"
                        v-model="form.symptoms"
                        value="Epilepsie" />

                    <div class="">Epilepsie</div>
                </div>
            </div>

            <div class="grid grid-cols-4 gap-4 my-4">
                <div class="flex items-center">
                    <div class="flex items-center gap-4">
                        <Checkbox
                            v-model="form.symptoms"
                            :disabled="form.is_no_findings == true"
                            value="Krebs" />

                        <div class="">Krebs</div>
                    </div>
                </div>

                <div class="">
                    <div class="flex items-center gap-2">
                        <span>wann:</span>

                        <InputMask
                            :disabled="form.is_no_findings == true"
                            :invalid="v$.form.cancer_diagnosed_date.$error"
                            v-model="form.cancer_diagnosed_date"
                            mask="99.9999"
                            placeholder="mm.jjjj"
                            @input="v$.form.cancer_diagnosed_date.$touch"
                            @blur="v$.form.cancer_diagnosed_date.$touch"
                            class="block w-full" />
                    </div>

                    <div v-if="v$.form.cancer_diagnosed_date.$error" class="text-sm text-[#dc2626]">
                        {{ v$.form.cancer_diagnosed_date.$errors[0].$message }}
                    </div>
                </div>

                <div class="col-span-2">
                    <InputText
                        :disabled="form.is_no_findings == true"
                        :invalid="v$.form.cancer_diagnosed_location.$error"
                        v-model="form.cancer_diagnosed_location"
                        type="text"
                        class="w-full"
                        placeholder="welche"
                        @input="v$.form.cancer_diagnosed_location.$touch"
                        @blur="v$.form.cancer_diagnosed_location.$touch"
                    />

                    <div v-if="v$.form.cancer_diagnosed_location.$error" class="text-sm text-[#dc2626]">
                        {{ v$.form.cancer_diagnosed_location.$errors[0].$message }}
                    </div>
                </div>
            </div>

            <div class="my-4">
                <div class="flex items-center gap-4">
                    <Checkbox
                        :disabled="form.is_no_findings == true"
                        v-model="form.symptoms"
                        value="Kälteassoziierte Störungen z.B. Raynaudsche Krankheit"
                    />

                    <div class="">
                        Kälteassoziierte Störungen z.B. Raynaudsche Krankheit
                    </div>
                </div>
            </div>

            <div class="my-4">
                <div class="flex items-center gap-4">
                    <div class="flex items-center gap-4">
                        <Checkbox
                            :disabled="form.is_no_findings == true"
                            v-model="form.symptoms"
                            value="Wurde innerhalb der letzten 2 Jahre eine Operation durchgeführt?"
                        />

                        <div class="">
                            Wurde innerhalb der letzten 2 Jahre eine Operation durchgeführt?
                        </div>
                    </div>

                    <div class="flex-1">
                        <InputText
                            :disabled="form.is_no_findings == true"
                            :invalid="v$.form.operation_details.$error"
                            v-model="form.operation_details"
                            type="text"
                            class="w-full"
                            placeholder="welche"
                            @input="v$.form.operation_details.$touch"
                            @blur="v$.form.operation_details.$touch"
                        />

                        <div v-if="v$.form.operation_details.$error" class="text-sm text-[#dc2626]">
                            {{ v$.form.operation_details.$errors[0].$message }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="my-10">
            <div class="flex items-center gap-4">
                <Checkbox
                    :disabled="form.symptoms.length"
                    :invalid="v$.form.is_no_findings.$error"
                    :binary="true"
                    v-model="form.is_no_findings"
                    @change="v$.form.is_no_findings.$touch"
                />

                <div class="font-bold">
                    Zur Zeit habe ich keine Beschwerden und es liegen keine Befunde vor
                </div>
            </div>

            <div v-if="v$.form.is_no_findings.$error" class="text-sm text-[#dc2626]">
                {{ v$.form.is_no_findings.$errors[0].$message }}
            </div>
        </div>

        <div class="my-7">
            <div class="font-bold">Nur für Frauen:</div>
            <div class="grid grid-cols-1 gap-4 my-4">
                <div class="" v-for="(item, i) in onlyForWomen" :key="i">
                    <div class="flex items-center gap-4">
                        <Checkbox v-model="form.woman_details" :value="item" />
                        <div class="">{{ item }}</div>
                    </div>
                </div>
            </div>
        </div>

        <div class="flex mt-10 justify-end">
            <Button
                label="weiter"
                class="min-w-[100px] !bg-black !text-[#FFF053] !border-none"
                @click="emits('next', 'medicines', 'symptoms-and-diseases')"
            />
        </div>
    </div>
</template>
